<template>
  <div v-if="!hasPermission('Create Subscriber')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar>
      <template #actions>
        <router-link to="/plans" class="btn btn-sm btn-light-primary">
          <i class="bi bi-arrow-left me-1"></i> Planos
        </router-link>
      </template>
    </Toolbar>

  <div class="container-xxl">
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          <div class="d-flex align-items-center gap-3 my-1">
            <div class="d-flex align-items-center position-relative">
              <span class="svg-icon svg-icon-1 position-absolute ms-6">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                         fill="none">
                      <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1"
                            transform="rotate(45 17.0365 15.1223)" fill="black"></rect>
                      <path
                        d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z"
                        fill="black"></path>
                    </svg>
                </span>
              <input type="search" v-model="search"
                     class="form-control form-control-solid w-250px ps-14"
                     placeholder="Nome ou documento...">
            </div>

            <select v-model="selectedPlanId" @change="loadSubscribers"
                    class="form-control form-control-solid w-250px">
              <option value="">Todos os planos</option>
              <option :value="plan.id_plan" v-for="plan in plans" :key="plan.id_plan">
                {{ plan.plan_name }}
              </option>
            </select>

            <select v-model="selectedSubscriberType" @change="loadSubscribers"
                    class="form-control form-control-solid w-200px">
              <option value="">Todos os tipos</option>
              <option value="individual">Individual</option>
              <option value="company">Empresarial</option>
            </select>

            <MultiSelect
              v-model="selectedStatuses"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              :multiple="true"
              :maxSelectedLabels="3"
              selectedItemsLabel="{0} status selecionados"
              placeholder="Selecione os status"
              class="form-control form-control-solid form-control-sm d-flex align-items-center w-300px"
              @change="loadSubscribers"
            />
          </div>
        </div>
        <div class="card-toolbar">
          <button v-if="hasPermission('Create Subscriber')" class="btn btn-primary" @click="newSubscriber">
            Nova Assinatura
          </button>
        </div>
      </div>
      <div class="card-body py-4">
        <div class="table-responsive">
          <table class="table table-hover table-row-bordered">
            <thead>
            <tr class="fw-bold fs-6 text-muted">
              <th>Nome completo</th>
              <th>Telefone</th>
              <th>Plano</th>
              <th>Forma de pagamento</th>
              <th>Nro. Dependentes</th>
              <th>Status</th>
              <th class="text-center">Situação</th>
              <th class="text-center">Ações</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="subscriber in subscribers.data">
              <td class="d-flex align-items-center">
                <div class="d-flex flex-column">
                  <span class="text-gray-800 text-hover-primary mb-1">{{ subscriber.user.user_name }}</span>
                  <span>{{ subscriber.user.document_number || subscriber.user.user_cpf }}</span>
                </div>
              </td>
              <td>{{ maskIt(subscriber.user.user_phone, '(##) #####-####') }}</td>
              <td>
                {{ subscriber.plan.plan_name }}
              </td>
              <td>
                {{ subscriber.plan_payment_method_name || paymentMethodLabel(subscriber.plan_payment_method) || '-' }}
              </td>
              <td>
                {{ subscriber.dependents_count }}
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span>{{ subscriber.subs_status_dsc }}</span>
                  <template v-if="subscriber.subs_status === 'cancelled'">
                    <span
                      v-if="subscriber.cancelled_by?.user_name"
                      class="text-muted fs-8"
                    >
                      Cancelado por {{ subscriber.cancelled_by.user_name }}
                    </span>
                    <span
                      v-if="subscriber.cancelled_at"
                      class="text-muted fs-8"
                    >
                      em {{ date(subscriber.cancelled_at).format('DD/MM/YYYY HH:mm') }}
                    </span>
                  </template>
                </div>
              </td>
              <td class="text-center">
                <span v-if="subscriber.charges_overdue_count > 0"
                      class="badge badge-danger fs-7">
                  Em atraso
                </span>
                <span v-else
                      class="badge badge-success fs-7">
                  Em dia
                </span>
              </td>
              <td class="text-center" style="min-width: 160px;">
                <button v-if="hasPermission('Update Subscriber')" class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                        type="button" @click="editSubscriber(subscriber)"
                        title="Editar assinatura">
                  <i class="bi bi-pen"></i>
                </button>
                <button v-if="subscriber.subscriber_type !== 'company'"
                        class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                        type="button" @click="showCharges(subscriber)"
                        title="Visualizar Faturas / Cobranças">
                  <i class="bi bi-cash-coin"></i>
                </button>
                <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                        type="button" @click="downloadContract(subscriber)"
                        title="Baixar Contrato"
                        v-if="subscriber.subscriber_type !== 'company' && subscriber.contract">
                  <i class="bi bi-file-earmark-arrow-down"></i>
                </button>
                <button v-if="canShowPixButton(subscriber)"
                        class="btn btn-sm btn-icon btn-bg-light btn-active-color-info me-1 border border-gray-400"
                        type="button" @click="openPixModal(subscriber)"
                        title="Ver QR Code PIX">
                  <i class="bi bi-qr-code"></i>
                </button>
                <button v-if="hasPermission('Update Subscriber') && subscriber.subs_status !== 'cancelled'"
                        class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger me-1 border border-gray-400"
                        type="button" @click="cancelSubscriber(subscriber)"
                        title="Cancelar Assinatura">
                  <i class="bi bi-x-circle"></i>
                </button>
                <button v-if="hasPermission('Update Subscriber') && subscriber.subs_status === 'cancelled'"
                        class="btn btn-sm btn-icon btn-bg-light btn-active-color-success me-1 border border-gray-400"
                        type="button" @click="reactivateSubscriber(subscriber)"
                        title="Reativar Assinatura">
                  <i class="bi bi-arrow-counterclockwise"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <Pagination :data="subscribers"/>
        </div>
      </div>
    </div>

    <SubscriberModal
      :subscriber="selectedSubscriber"
      @onClose="selectedSubscriber = null"
      @subscriberSaved="loadSubscribers"
      @openPixModal="handleOpenPixModal"
    />

    <ChargesModal
      :subscriber="selectedSubscriber"
      @onClose="selectedSubscriber = null"
      @openPixModal="handleOpenPixModal"
      @chargesUpdated="loadSubscribers"
    />

    <PixPaymentModal
      ref="pixPaymentModal"
      :pixData="pixModalData"
      @onClose="pixModalData = null"
      @paymentConfirmed="handlePixPaymentConfirmed"
    />
  </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Pagination from "../../components/Pagination";
import strMask from "../../helpers/strMask";
import str from "../../helpers/str";
import Permission from "../../helpers/permission";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import SubscriberModal from "@/views/subscriber/SubscriberModal.vue";
import Modal from "@/helpers/modal";
import ChargesModal from "@/views/subscriber/ChargesModal.vue";
import PixPaymentModal from "@/components/PixPaymentModal.vue";
import Download from "@/helpers/download";
import Loader from "@/helpers/loader";
import Alert from "@/helpers/alert";
import MultiSelect from 'primevue/multiselect';
import debounce from "../../helpers/debounce";

export default {
  name: "Subscriber",
  components: {
    ChargesModal,
    SubscriberModal,
    PixPaymentModal,
    Pagination,
    Toolbar,
    MultiSelect,
  },
  data() {
    return {
      modalKey: Date.now().toString(),
      search: '',
      selectedSubscriber: null,
      subscribers: [],
      plans: [],
      selectedPlanId: '',
      selectedSubscriberType: '',
      selectedStatuses: ['active', 'suspended', 'pending'],
      statusOptions: [
        { value: 'active', label: 'Ativo' },
        { value: 'suspended', label: 'Suspenso' },
        { value: 'pending', label: 'Pendente' },
        { value: 'payment_failed', label: 'Pagamento Falhou' },
        { value: 'cancelled', label: 'Cancelado' },
        { value: 'expired', label: 'Expirado' },
      ],
      pixModalData: null,
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  created() {
    this.debouncedLoadSubscribers = debounce(() => {
      this.resetPage();
      this.loadSubscribers(1);
    }, 400);
  },
  mounted() {
    this.loadPlans();
    if (this.$route.query.plan_id) {
      this.selectedPlanId = this.$route.query.plan_id;
    }
    this.resetPage();
    this.loadSubscribers();
  },
  watch: {
    '$route.query.page'() {
      this.loadSubscribers();
    },
    search() {
      this.debouncedLoadSubscribers();
    },
  },
  methods: {
    date: moment,
    url(path) {
      return str.url(path)
    },
    maskIt(value, mask) {
      return strMask.it(value, mask);
    },
    loadPlans() {
      http.get(`/places/${this.place.id_place}/plans`, { params: { per_page: 1000 } })
        .then((response) => {
          this.plans = response.data?.data || response.data || [];
        });
    },
    paymentMethodLabel(method) {
      const labels = {
        credit_card: 'Cartão de crédito',
        pix: 'PIX',
        bank_slip: 'Boleto',
        money: 'Dinheiro'
      };
      return method ? (labels[method] || method) : '-';
    },
    loadSubscribers(pageOverride) {
      const page = pageOverride !== undefined ? pageOverride : this.$route.query.page;
      this.subscribers = [];
      http.get(`/places/${this.place.id_place}/subscribers`, {
        params: {
          'page': page,
          'search': this.search,
          'per_page': 10,
          'statuses': this.selectedStatuses,
          'id_plan': this.selectedPlanId,
        }
      }).then((response) => {
        this.subscribers = response.data;
      });
    },
    newSubscriber() {
      this.selectedSubscriber = null;
      this.modalKey = Date.now().toString();

      Modal.toggle('#subscriberModal');
    },
    showCharges(selectedSubscriber) {
      this.selectedSubscriber = selectedSubscriber;

      Modal.toggle('#chargesModal');
    },
    editSubscriber(selectedSubscriber) {
      this.modalKey = Date.now().toString();
      this.selectedSubscriber = selectedSubscriber;

      Modal.toggle('#subscriberModal');
    },
    resetPage() {
      const query = {...this.$route.query, page: 1};
      this.$router.push({path: this.$route.path, query});
    },
    hasPermission(permission) {
      return Permission.has(permission);
    },
    downloadContract(subscriber) {
      if (!subscriber.contract) {
        return;
      }

      const contract = subscriber.contract;
      Loader.show('Baixando contrato, por favor aguarde...');

      http.get(`/places/${this.place.id_place}/contracts/${contract.id_contract}/download`, {
        responseType: 'blob'
      })
      .then((response) => {
        Download.downloadFile(response.data, `contrato-${subscriber.user.user_name}.pdf`, 'application/pdf');
        Loader.close();
      })
      .catch((error) => {
        console.error('Erro ao baixar contrato:', error);
        Loader.close();
        Alert.error('Erro ao baixar contrato');
      });
    },
    cancelSubscriber(subscriber) {
      Swal.fire({
        title: 'Cancelar Assinatura',
        html: `
          <div style="text-align: left; margin-bottom: 20px;">
            <p style="margin-bottom: 15px; color: #333; font-size: 15px;">
              Tem certeza que deseja cancelar a assinatura de <strong>${subscriber.user.user_name}</strong>?
            </p>
            <p style="margin-bottom: 10px; color: #555; font-weight: 600; font-size: 14px;">Esta ação irá:</p>
            <ul style="margin-left: 20px; margin-bottom: 20px; color: #555; font-size: 14px; line-height: 1.8;">
              <li>Cancelar a assinatura</li>
              <li>Cancelar todos os contratos vinculados</li>
              <li>Cancelar todas as cobranças pendentes</li>
              <li>Impedir o envio de novas notificações e cobranças</li>
            </ul>
          </div>
          <div style="text-align: left;">
            <label style="display: block; margin-bottom: 8px; color: #333; font-weight: 600; font-size: 14px;">
              Motivo do cancelamento: <span style="color: #d33;">*</span>
            </label>
            <textarea 
              id="cancellation_reason" 
              placeholder="Digite o motivo do cancelamento..." 
              required
              style="width: 100%; min-height: 100px; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; resize: vertical; box-sizing: border-box; font-family: inherit;"
            ></textarea>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        focusConfirm: false,
        width: '550px',
        preConfirm: () => {
          const reason = document.getElementById('cancellation_reason').value.trim();
          if (!reason) {
            Swal.showValidationMessage('Por favor, informe o motivo do cancelamento.');
            return false;
          }
          if (reason.length > 1000) {
            Swal.showValidationMessage('O motivo não pode ter mais de 1000 caracteres.');
            return false;
          }
          return reason;
        }
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Cancelando assinatura...', 'Por favor, aguarde.');
          
          http.patch(`/places/${this.place.id_place}/subscribers/${subscriber.id_subscriber}/cancel`, {
            cancellation_reason: result.value
          })
          .then(() => {
            Loader.close();
            Alert.success('Assinatura cancelada com sucesso.');
            this.loadSubscribers();
          })
          .catch((error) => {
            Loader.close();
            const errorMessage = error.response?.data?.message || error.response?.data?.subscriber?.[0] || 'Erro ao cancelar assinatura.';
            Alert.error(errorMessage);
          });
        }
      });
    },
    reactivateSubscriber(subscriber) {
      Swal.fire({
        title: 'Reativar Assinatura',
        text: `Deseja reativar a assinatura de "${subscriber.user.user_name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reativar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#198754',
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }

        Loader.show('Reativando assinatura...', 'Por favor, aguarde.');

        http.patch(`/places/${this.place.id_place}/subscribers/${subscriber.id_subscriber}/reactivate`)
          .then(() => {
            Loader.close();
            Alert.success('Assinatura reativada com sucesso.');
            this.loadSubscribers();
          })
          .catch((error) => {
            Loader.close();
            const errorMessage = error.response?.data?.message
              || error.response?.data?.subscriber?.[0]
              || 'Erro ao reativar assinatura.';
            Alert.error(errorMessage);
          });
      });
    },
    canShowPixButton(subscriber) {
      return !!subscriber.pix_payment_available;
    },
    async openPixModal(subscriber) {
      try {
        Loader.show('Carregando dados do PIX...', 'Por favor, aguarde.');
        
        // Buscar dados do PIX via API
        const response = await http.get(`/pix/${subscriber.id_subscriber}/data`);
        
        Loader.close();
        
        if (response.data) {
          // Verificar se PIX ainda é válido (não expirado)
          const expiresAt = response.data.expires_at;
          if (expiresAt && new Date(expiresAt) < new Date()) {
            Alert.error('Este código PIX expirou.');
            return;
          }
          
          // Verificar se já foi pago
          if (response.data.status === 'S') {
            Alert.toastInfo('Este PIX já foi pago.');
            this.loadSubscribers(); // Recarregar para atualizar status
            return;
          }
          
          this.pixModalData = {
            pix_code: response.data.pix_code,
            qr_code_base64: response.data.pix_qrcode_img,
            payment_id: response.data.id_card_trans,
            subscription_id: subscriber.id_subscriber,
            expires_at: expiresAt || response.data.pix_expires_at || null,
          };
          
          this.$nextTick(() => {
            this.$refs.pixPaymentModal?.open();
          });
        }
      } catch (error) {
        Loader.close();
        console.error('Erro ao carregar dados do PIX:', error);
        Alert.error(error.response?.data?.error || 'Erro ao carregar dados do PIX.');
      }
    },
    handleOpenPixModal(pixData) {
      this.pixModalData = pixData;
      this.$nextTick(() => {
        this.$refs.pixPaymentModal?.open();
      });
    },
    handlePixPaymentConfirmed() {
      // Recarregar lista de assinaturas quando PIX for pago
      this.loadSubscribers();
      Alert.success('PIX pago com sucesso! A assinatura foi ativada.');
    }
  }
}
</script>

<style scoped>

</style>
